import { ImxRewards } from "@/components/icons/ImxRewards";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ChevronRight, Heart, LoaderPinwheel } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import {
  type ActivationQuest,
  QuestType,
  getQuestIcon,
  getQuestText,
} from "./ActivationPanel.utils";

const ItemContainer = ({
  quest,
  children,
}: {
  quest: ActivationQuest;
  children: React.ReactNode;
}) => {
  const baseClassName =
    "text-start w-full flex justify-between items-center p-3 bg-neutral-100/5 text-muted-foreground text-xs @3xl:text-sm transition-all hover:shadow-lg hover:bg-neutral-800/20";

  if (quest.completed && quest.ab) {
    return (
      <Link className={baseClassName} href={quest.ab}>
        {children}
      </Link>
    );
  }

  if (
    quest.onClick &&
    quest.type !== QuestType.FREE_SPIN &&
    quest.type !== QuestType.WISHLIST
  ) {
    return (
      <button className={baseClassName} onClick={quest.onClick} type="button">
        {children}
      </button>
    );
  }

  return <div className={baseClassName}>{children}</div>;
};

export const QuestItem = ({
  quest,
  allQuestsCompleted = false,
  spinsAvailable,
  isNextQuest = false,
}: {
  quest: ActivationQuest;
  allQuestsCompleted?: boolean;
  spinsAvailable?: number;
  isNextQuest?: boolean;
}) => {
  const { text, subText } = getQuestText(quest);
  const shouldGlowIcon = isNextQuest && !quest.completed;

  return (
    <ItemContainer quest={quest}>
      <div className={cn("flex items-center gap-x-3")}>
        {quest.type === QuestType.FREE_SPIN && allQuestsCompleted ? null : (
          <div
            className={cn("rounded-lg", shouldGlowIcon && "animate-icon-glow")}
          >
            {getQuestIcon(quest.type, quest.completed, quest.loading)}
          </div>
        )}
        {quest.type === QuestType.FREE_SPIN ? (
          <div className="pr-4">
            <p className="font-bold text-foreground">REWARD</p>
            <p className={allQuestsCompleted ? "text-status-success" : ""}>
              {allQuestsCompleted
                ? "You've unlocked your reward!"
                : "Unlock 5x Spins!"}
            </p>
          </div>
        ) : quest.completed ? (
          <div className="pr-4">
            <p
              className={
                quest.type === QuestType.BONUS_GEMS ? "text-status-success" : ""
              }
            >
              {text}
            </p>
            {quest.type === QuestType.BONUS_GEMS ? null : (
              <p className="text-status-success">
                Keep questing with {quest.shortName || quest.name}!
              </p>
            )}
          </div>
        ) : (
          <div className="pr-4">
            <p className="font-[550] text-foreground">{text}</p>{" "}
            <p>{subText}</p>
          </div>
        )}
      </div>

      <div className="flex items-center gap-x-2">
        {quest.gems ? (
          <p
            className={cn(
              "rounded-md border font-[550] px-1.5 bg-neutral-900/20 flex items-center gap-x-1 whitespace-nowrap",
              quest.completed
                ? "border-status-success text-status-success fill-status-success"
                : "border-white/10 text-foreground fill-foreground",
            )}
          >
            {quest.gems} <ImxRewards className="size-3" />
          </p>
        ) : null}

        {/* Follow Button for WISHLIST quests - Only shown when not completed */}
        {quest.type === QuestType.WISHLIST && !quest.completed ? (
          <Button
            size="sm"
            onClick={quest.onClick}
            disabled={quest.loading}
            className="text-xs px-3 py-1.5 min-h-7"
          >
            {quest.loading ? (
              "Following..."
            ) : (
              <>
                <Heart className="size-3.5" />
                Follow
              </>
            )}
          </Button>
        ) : null}

        {/* Free Spin Button */}
        {quest.type === QuestType.FREE_SPIN && allQuestsCompleted ? (
          spinsAvailable === 0 ? (
            <Button
              size="sm"
              disabled
              className="bg-status-success hover:bg-status-success px-3 py-1.5 min-h-7 text-xs"
            >
              <LoaderPinwheel className="size-4 mr-1" />
              Claimed
            </Button>
          ) : (
            <div className="animate-golden-glow rounded-full">
              <Button onClick={quest.onClick} size="sm">
                <LoaderPinwheel className="size-4" />
                Claim Spins • {spinsAvailable}
              </Button>
            </div>
          )
        ) : null}

        {quest.type === QuestType.FREE_SPIN && !allQuestsCompleted ? (
          <div className="relative w-28 h-8">
            <Image
              src="/images/prize-wheel-half.webp"
              alt="Spinner Wheel"
              width={196}
              height={97}
              className="absolute right-0 -top-3"
            />
          </div>
        ) : null}

        {quest.type === QuestType.BONUS_GEMS ||
        quest.type === QuestType.FREE_SPIN ||
        (quest.type === QuestType.WISHLIST && !quest.completed) ? null : (
          <ChevronRight className="size-5 stroke-muted-foreground" />
        )}
      </div>
    </ItemContainer>
  );
};
